/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * A field an application declared inside the file itself.
 *
 * Developer fields are FIT's sanctioned extension mechanism: a producer emits a
 * `developer_data_id` message to claim an index and a `field_description`
 * message to describe each field, and from then on those fields ride along in
 * ordinary data records. Redefining an existing profile field instead would
 * break every other reader, which is precisely what this exists to avoid.
 */
public class DeveloperField(
    override val fieldName: String,
    override val fieldNum: UByte,
    override val baseType: BaseType,
    public val developerDataIndex: UByte,
    override val units: String = "",
    override val scale: Double = Fit.FIELD_DEFAULT_SCALE,
    override val offset: Double = Fit.FIELD_DEFAULT_OFFSET,
    /** Profile field this one shadows, when the description names one. */
    public val nativeFieldNum: UByte? = null,
    applicationId: ByteArray? = null,
) : FieldBase() {
    // Copied in, and copied out again by the getter below. A ByteArray is
    // mutable and has reference equality, so holding the caller's array would
    // let whoever passed it rewrite this field's identity afterwards.
    private val applicationIdBytes: ByteArray? = applicationId?.copyOf()

    /**
     * Application UUID from the declaring `developer_data_id` message.
     *
     * A fresh copy on every read: writing into what this returns changes
     * nothing, because the UUID is what [key] identifies this field by and a
     * field whose identity can be edited from outside is not an identity.
     */
    public val applicationId: ByteArray? get() = applicationIdBytes?.copyOf()

    /** Identity of this field across the file: application, index and number. */
    public val key: DeveloperDataKey
        get() = DeveloperDataKey(applicationIdBytes?.toList(), developerDataIndex, fieldNum)

    /**
     * Copies the description and the values, sharing nothing mutable with
     * [other] — the application UUID included.
     */
    internal constructor(other: DeveloperField) : this(
        other.fieldName,
        other.fieldNum,
        other.baseType,
        other.developerDataIndex,
        other.units,
        other.scale,
        other.offset,
        other.nativeFieldNum,
        other.applicationIdBytes,
    ) {
        declaredTypeId = other.declaredTypeId
        values.addAll(other.values)
    }

    /**
     * Equality includes the declaring application, on top of what [FieldBase]
     * compares: two files can both define field 0 under developer data index 0,
     * and only the application UUID tells them apart.
     */
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is DeveloperField) return false
        return super.equals(other) && key == other.key
    }

    override fun hashCode(): Int = 31 * super.hashCode() + key.hashCode()

    override fun toString(): String = "$fieldName(dev #$developerDataIndex/$fieldNum)=$values"
}

/**
 * What makes a developer field unique.
 *
 * The developer data index alone is not enough: two applications in the same
 * file can both claim index 0 in different `developer_data_id` messages, so the
 * application UUID has to take part.
 */
public data class DeveloperDataKey(
    public val applicationId: List<Byte>?,
    public val developerDataIndex: UByte,
    public val fieldDefinitionNumber: UByte,
)
